/**
 * Minimal SQLite compatibility layer for web using localStorage
 * Covers major CRUD operations and basic transaction support
 */

export class SQLiteDatabase {
    private dbName: string;
    private inTransaction: boolean = false;
    private transactionQueue: (() => void)[] = [];

    constructor(dbName: string) {
        this.dbName = dbName;
    }

    async execAsync(sql: string): Promise<void> {
        // Execute SQL (mainly for CREATE TABLE statements)
        // In web, we don't need to create tables
        // console.log(`Executed: ${sql}`);
    }

    getFirstSync<T>(sql: string): T | null {
        // Simple key-value get operation
        const match = sql.match(/WHERE\s+(\w+)\s*=\s*['"]([^'"]+)['"]/i);
        if (match) {
            const [, field, value] = match;
            const key = `${this.dbName}_${field}_${value}`;
            const data = localStorage.getItem(key);
            return data ? JSON.parse(data) : null;
        }
        return null;
    }

    getFirstAsync<T>(sql: string): Promise<T | null> {
        return new Promise((resolve) => {
            resolve(this.getFirstSync<T>(sql));
        });
    }

    getAllSync<T>(sql: string): T[] {
        // Get all items matching a pattern
        const tableMatch = sql.match(/FROM\s+(\w+)/i);
        if (tableMatch) {
            const table = tableMatch[1];
            const prefix = `${this.dbName}_${table}_`;
            const results: T[] = [];
            
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(prefix)) {
                    const data = localStorage.getItem(key);
                    if (data) {
                        results.push(JSON.parse(data));
                    }
                }
            }
            return results;
        }
        return [];
    }

    getAllAsync<T>(sql: string): Promise<T[]> {
        return new Promise((resolve) => {
            resolve(this.getAllSync<T>(sql));
        });
    }

    async runAsync(sql: string, params?: any[]): Promise<any> {
        // Handle INSERT/UPDATE/DELETE operations
        if (this.inTransaction) {
            this.transactionQueue.push(() => this.executeSql(sql, params));
            return { rows: [] };
        }
        
        return this.executeSql(sql, params);
    }

    private executeSql(sql: string, params?: any[]): any {
        const upperSql = sql.toUpperCase();
        
        if (upperSql.includes('INSERT') || upperSql.includes('REPLACE')) {
            // Simple INSERT operation
            const tableMatch = sql.match(/INTO\s+(\w+)/i);
            const fieldsMatch = sql.match(/\(([^)]+)\)/);
            
            if (tableMatch && fieldsMatch && params) {
                const table = tableMatch[1];
                const fields = fieldsMatch[1].split(',').map(f => f.trim());
                const data: any = {};
                
                fields.forEach((field, index) => {
                    data[field] = params[index];
                });
                
                // Use first field as key
                const key = `${this.dbName}_${table}_${params[0]}`;
                localStorage.setItem(key, JSON.stringify(data));
            }
        } else if (upperSql.includes('DELETE')) {
            // Simple DELETE operation
            const tableMatch = sql.match(/FROM\s+(\w+)/i);
            const whereMatch = sql.match(/WHERE\s+(\w+)\s*=\s*\?/i);
            
            if (tableMatch && whereMatch && params) {
                const table = tableMatch[1];
                const key = `${this.dbName}_${table}_${params[0]}`;
                localStorage.removeItem(key);
            }
        }
        
        return { rows: [] };
    }

    async transactionAsync(callback: () => Promise<void>): Promise<void> {
        this.inTransaction = true;
        this.transactionQueue = [];
        
        try {
            await callback();
            // Execute all queued operations
            this.transactionQueue.forEach(op => op());
            this.transactionQueue = [];
        } catch (error) {
            // Rollback (in localStorage, we just clear the queue)
            this.transactionQueue = [];
            throw error;
        } finally {
            this.inTransaction = false;
        }
    }

    isInTransactionSync(): boolean {
        return this.inTransaction;
    }

    closeSync(): void {
        // No-op for web
    }

    async closeAsync(): Promise<void> {
        // No-op for web
    }
}

// SQLite compatibility exports
export const SQLite = {
    openDatabaseAsync: async (dbName: string): Promise<SQLiteDatabase> => {
        return new SQLiteDatabase(dbName);
    }
};